import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const NOTIFICATION_TYPES = [
  "booking_accepted",
  "payment_received",
  "shift_reminder_24h",
  "shift_reminder_2h",
  "shift_starting_soon",
  "shift_completed",
  "payout_sent",
  "review_request",
  "review_received",
  "shift_rescheduled",
  "shift_cancelled",
  "no_show_reported",
  "arrival_dispute_submitted",
  "arrival_dispute_resolved",
  "booking_receipt",
  "support_ticket_created",
  "support_ticket_status_changed",
  "support_ticket_note_added",
] as const;
const CHANNELS = ["in_app", "email", "sms"] as const;

export type NotificationRow = {
  id: string;
  type: (typeof NOTIFICATION_TYPES)[number];
  title: string;
  body: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

export type PreferenceRow = {
  type: (typeof NOTIFICATION_TYPES)[number];
  channel: (typeof CHANNELS)[number];
  enabled: boolean;
};

export const listNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ items: NotificationRow[]; unread: number }> => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("notifications" as never)
      .select("id,type,title,body,link,read_at,created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);
    const items = (data as NotificationRow[] | null) ?? [];
    const unread = items.filter((n) => !n.read_at).length;
    return { items, unread };
  });

export const markNotificationRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await context.supabase
      .from("notifications" as never)
      .update({ read_at: new Date().toISOString() } as never)
      .eq("id", data.id)
      .eq("user_id", context.userId);
    return { ok: true };
  });

export const markAllNotificationsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await context.supabase
      .from("notifications" as never)
      .update({ read_at: new Date().toISOString() } as never)
      .eq("user_id", context.userId)
      .is("read_at", null);
    return { ok: true };
  });

export const getNotificationPreferences = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PreferenceRow[]> => {
    const { data } = await context.supabase
      .from("notification_preferences" as never)
      .select("type,channel,enabled")
      .eq("user_id", context.userId);
    const stored = (data as PreferenceRow[] | null) ?? [];
    const map = new Map(stored.map((r) => [`${r.type}:${r.channel}`, r.enabled]));
    const all: PreferenceRow[] = [];
    for (const type of NOTIFICATION_TYPES) {
      for (const channel of CHANNELS) {
        all.push({ type, channel, enabled: map.get(`${type}:${channel}`) ?? true });
      }
    }
    return all;
  });

export const updateNotificationPreference = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      type: z.enum(NOTIFICATION_TYPES),
      channel: z.enum(CHANNELS),
      enabled: z.boolean(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    // booking_accepted, payment_received, payout_sent must stay on for in_app (account-critical)
    const mandatory =
      data.channel === "in_app" &&
      ["booking_accepted", "payment_received", "payout_sent"].includes(data.type);
    if (mandatory && !data.enabled) {
      throw new Error("Account-critical in-app alerts cannot be disabled.");
    }
    const { error } = await context.supabase
      .from("notification_preferences" as never)
      .upsert(
        {
          user_id: context.userId,
          type: data.type,
          channel: data.channel,
          enabled: data.enabled,
          updated_at: new Date().toISOString(),
        } as never,
        { onConflict: "user_id,type,channel" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });
